import React from "react";
import { formatDate } from "./useDueDates";

/**
 * Asks the user whether a bulk date should overwrite deadlines already in force.
 *
 * A bulk fill never silently changes a KPI that already has a due date. When the
 * selection includes such KPIs this dialog states exactly how many, and lets the
 * user choose: overwrite them too, or — when the selection is mixed — fill only
 * the blank rows. When every selected KPI already has a date, the only forward
 * action is to overwrite.
 * @param {object} props
 * @param {string} props.date the bulk date being applied, as `YYYY-MM-DD`.
 * @param {number} props.eligibleCount KPIs with no deadline yet (blank rows).
 * @param {number} props.withDateCount KPIs that already have a deadline.
 * @param {() => void} props.onOverwriteAll stage the date on every selected KPI.
 * @param {() => void} props.onFillBlanks stage the date only on the blank rows.
 * @param {() => void} props.onCancel dismiss without staging anything.
 * @returns {JSX.Element}
 */
export default function BulkOverwriteModal({
  date,
  eligibleCount,
  withDateCount,
  onOverwriteAll,
  onFillBlanks,
  onCancel,
}) {
  const allHaveDates = eligibleCount === 0;

  return (
    <div
      className="ddt-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="ddt-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Overwrite existing due dates"
      >
        <div className="ddt-modal-head">
          <h3 className="ddt-modal-title">Overwrite existing due dates?</h3>
          <button
            type="button"
            className="ddt-modal-close"
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>

        <p className="ddt-modal-current">
          {allHaveDates
            ? `All ${withDateCount} selected KPI${
                withDateCount !== 1 ? "s" : ""
              } already have a due date.`
            : `${eligibleCount} selected KPI${
                eligibleCount !== 1 ? "s" : ""
              } will get the new date. ${withDateCount} already have a due date.`}
        </p>

        <p className="ddt-modal-hint">
          {allHaveDates
            ? "Overwrite them with "
            : "Overwrite those existing dates with "}
          <strong>{formatDate(date)}</strong>?
        </p>

        <div className="ddt-modal-actions">
          <button
            type="button"
            className="ddt-btn ddt-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          {!allHaveDates && (
            <button
              type="button"
              className="ddt-btn ddt-btn-ghost"
              onClick={onFillBlanks}
            >
              Fill blanks only
            </button>
          )}
          <button type="button" className="ddt-btn" onClick={onOverwriteAll}>
            {allHaveDates ? "Overwrite" : "Overwrite all"}
          </button>
        </div>
      </div>
    </div>
  );
}
